import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags } from './helpers';

export const foodBistro: TemplateRegistration = {
  meta: {
    id: 'food-bistro',
    name: 'Food & Bistro',
    category: 'Creative',
    description: 'Warm, appetizing restaurant design with rich colors and elegant serif typography',
    previewImage: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
    themeEditable: false,
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Playfair Display', 'Lato']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} &middot; ${reviewCount} reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      const aboutSentences = safeAbout.split(/(?<=[.!?])\s+/).filter(s => s.length > 0);
      const timelineItems = aboutSentences.length > 1
        ? aboutSentences.map(s => `<div class="about-timeline-item"><p class="about-text">${s}</p></div>`).join('')
        : `<div class="about-timeline-item"><p class="about-text">${safeAbout}</p></div>`;
      sections.push(`
      <section id="about" class="section">
        <div class="container">
          <div class="section-header">
            <div class="decorative-flourish">&laquo; &diams; &raquo;</div>
            <h2 class="section-title">Our Story</h2>
          </div>
          <div class="about-timeline">
            ${timelineItems}
          </div>
        </div>
      </section>`);
    }

    // --- Services / Menu ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const menuItems = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="menu-item">
            <div class="menu-item-header">
              <h3>${sName}</h3>
              <span class="menu-dots"></span>
              ${s.price ? `<span class="menu-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
            <p class="menu-desc">${sDesc}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-darker">
        <div class="container">
          <div class="section-header">
            <div class="decorative-flourish">&laquo; &diams; &raquo;</div>
            <h2 class="section-title">Our Menu</h2>
          </div>
          <div class="menu-scroll">${menuItems}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map((url, i) => `<div class="gallery-item gallery-item-${(i % 3) + 1}"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section">
        <div class="container">
          <div class="section-header">
            <div class="decorative-flourish">&laquo; &diams; &raquo;</div>
            <h2 class="section-title">Gallery</h2>
          </div>
          <div class="gallery-masonry">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const items = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-inline">
            <p>&ldquo;${tContent}&rdquo;</p>
            <div class="t-attribution">&mdash; ${tName}${tRole ? `, ${tRole}` : ''}</div>
            ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-darker">
        <div class="container">
          <div class="section-header">
            <div class="decorative-flourish">&laquo; &diams; &raquo;</div>
            <h2 class="section-title">Guest Reviews</h2>
          </div>
          <div class="testimonials-inline">${items}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">FAQ</h2>
          </div>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="menu-item">
            <div class="menu-item-header">
              <h3>${pName}</h3>
              <span class="menu-dots"></span>
              <span class="menu-price">${pPrice}</span>
            </div>
            <p class="menu-desc">${pDesc}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-darker">
        <div class="container">
          <div class="section-header">
            <div class="decorative-flourish">&laquo; &diams; &raquo;</div>
            <h2 class="section-title">Pricing</h2>
          </div>
          <div class="menu-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">Hours</h2>
          </div>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-darker">
        <div class="container">
          <div class="section-header">
            <div class="decorative-flourish">&laquo; &diams; &raquo;</div>
            <h2 class="section-title">Reservations &amp; Contact</h2>
          </div>
          <div class="contact-stacked">
            <div class="contact-info-block">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">Get Directions</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Name" required>
              <input type="email" placeholder="Email" required>
              <input type="text" placeholder="Party Size">
              <textarea placeholder="Special Requests" rows="4" required></textarea>
              <button type="submit">Reserve a Table</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #1c1917; --bg-darker: #0f0e0c; --red: #b91c1c;
      --amber: #ca8a04; --cream: #fef2f2; --cream-muted: #c4b5a5;
      --border: #44403c; --font-heading: 'Playfair Display', Georgia, serif;
      --font-body: 'Lato', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--cream); line-height: 1.7; }
    .container { max-width: 1080px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--amber); text-decoration: none; transition: color .2s; }
    a:hover { color: var(--cream); }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(28,25,23,.95); backdrop-filter: blur(8px); border-bottom: 1px solid var(--border); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 1080px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-weight: 700; font-size: 1.3rem; color: var(--cream); letter-spacing: 1px; }
    .nav-links { display: flex; gap: 28px; list-style: none; }
    .nav-links a { color: var(--cream-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 400; transition: color .2s; }
    .nav-links a:hover { color: var(--amber); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 24px; height: 2px; background: var(--cream); margin: 5px 0; transition: .3s; }

    /* HERO - full-bleed with bottom info bar */
    .hero { position: relative; min-height: 100vh; display: flex; flex-direction: column; overflow: hidden; }
    .hero-image-fill { flex: 1; position: relative; overflow: hidden; }
    .hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center; }
    .hero-overlay { position: absolute; inset: 0; background: rgba(28,25,23,.15); }
    .hero-bottom-bar { position: relative; z-index: 2; background: var(--bg); padding: 20px 24px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
    .hero-bottom-bar-inner { max-width: 1080px; margin: 0 auto; width: 100%; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
    .hero-bottom-bar h1 { font-family: var(--font-heading); font-size: clamp(1.5rem, 3vw, 2rem); font-weight: 700; color: var(--cream); margin: 0; }
    .hero-cta { display: inline-block; padding: 12px 32px; background: var(--red); color: var(--cream); font-family: var(--font-heading); font-weight: 600; font-size: .95rem; letter-spacing: 1px; border: none; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(185,28,28,.3); color: var(--cream); }
    .hero-no-image .hero-image-fill { background: linear-gradient(135deg, var(--bg) 0%, #292524 100%); min-height: 60vh; }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-darker { background: var(--bg-darker); }
    .section-header { text-align: center; margin-bottom: 48px; }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.5rem, 3.5vw, 2.25rem); font-weight: 700; color: var(--cream); }
    .decorative-line { width: 50px; height: 2px; background: var(--red); margin: 16px 0; }
    .decorative-flourish { color: var(--amber); font-size: .85rem; letter-spacing: 4px; margin-bottom: 12px; }

    /* ABOUT - timeline style */
    .about-timeline { position: relative; max-width: 700px; margin: 0 auto; padding-left: 40px; }
    .about-timeline::before { content: ''; position: absolute; left: 16px; top: 0; bottom: 0; width: 2px; background: var(--red); }
    .about-timeline-item { position: relative; margin-bottom: 32px; padding-left: 24px; }
    .about-timeline-item::before { content: ''; position: absolute; left: -28px; top: 6px; width: 12px; height: 12px; background: var(--red); border-radius: 50%; border: 2px solid var(--bg); }
    .about-text { font-size: 1.05rem; color: var(--cream-muted); line-height: 1.9; }

    /* MENU / SERVICES - horizontal scroll-snap cards */
    .menu-scroll { display: flex; gap: 20px; overflow-x: auto; scroll-snap-type: x mandatory; padding-bottom: 16px; -webkit-overflow-scrolling: touch; }
    .menu-scroll::-webkit-scrollbar { height: 4px; }
    .menu-scroll::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
    .menu-item { flex: 0 0 280px; scroll-snap-align: start; padding: 28px 24px; border: 1px solid var(--border); background: var(--bg); }
    .menu-item-header { margin-bottom: 10px; }
    .menu-item-header h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; color: var(--cream); }
    .menu-dots { display: none; }
    .menu-price { font-family: var(--font-heading); font-size: 1.15rem; color: var(--amber); font-weight: 700; display: block; margin-top: 12px; }
    .menu-desc { color: var(--cream-muted); font-size: .9rem; font-style: italic; }

    /* GALLERY - masonry style */
    .gallery-masonry { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
    .gallery-item img { width: 100%; display: block; object-fit: cover; transition: opacity .3s; }
    .gallery-item:hover img { opacity: .85; }
    .gallery-item-1 img { height: 280px; }
    .gallery-item-2 img { height: 200px; }
    .gallery-item-3 img { height: 240px; }

    /* TESTIMONIALS - minimal inline (italic with em dash) */
    .testimonials-inline { max-width: 700px; margin: 0 auto; display: flex; flex-direction: column; gap: 32px; }
    .testimonial-inline { padding: 0; }
    .testimonial-inline p { font-size: 1.05rem; font-style: italic; color: var(--cream-muted); line-height: 1.8; }
    .testimonial-inline .t-attribution { margin-top: 8px; font-size: .9rem; color: var(--amber); font-family: var(--font-heading); font-style: normal; }
    .testimonial-stars { margin-top: 6px; }
    .star { color: var(--border); font-size: 1rem; }
    .star.filled { color: var(--amber); }

    /* FAQ */
    .faq-list { max-width: 700px; margin: 0 auto; }
    .faq-item { border-bottom: 1px solid var(--border); padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.05rem; font-weight: 700; margin-bottom: 10px; color: var(--cream); }
    .faq-a { color: var(--cream-muted); font-size: .9rem; line-height: 1.7; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; margin: 0 auto; text-align: center; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid var(--border); color: var(--cream-muted); font-size: .9rem; }
    .hours-list li:last-child { border-bottom: none; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--red); padding: 14px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: var(--cream); font-size: 1.1rem; }
    .review-badge .star { opacity: .5; }
    .review-badge .star.filled { opacity: 1; }
    .review-text { color: var(--cream); font-weight: 600; font-size: .9rem; }

    /* PROMO */
    .promo-banner { background: #292524; padding: 16px 0; border-bottom: 1px solid var(--border); }
    .promo-inner { display: flex; align-items: center; gap: 16px; justify-content: center; flex-wrap: wrap; }
    .promo-badge { background: var(--red); color: var(--cream); padding: 6px 18px; font-family: var(--font-heading); font-size: .85rem; font-weight: 700; }
    .promo-content strong { color: var(--cream); font-size: .9rem; }
    .promo-content span { color: var(--cream-muted); font-size: .85rem; margin-left: 6px; }

    /* CONTACT - info block on top, form below (stacked) */
    .contact-stacked { max-width: 640px; margin: 0 auto; }
    .contact-info-block { display: flex; gap: 24px; flex-wrap: wrap; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 1px solid var(--border); }
    .contact-item { display: flex; align-items: center; gap: 14px; color: var(--cream-muted); }
    .contact-item .icon-char { color: var(--amber); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--cream); }
    .map-link { display: inline-block; margin-top: 4px; font-weight: 600; font-size: .85rem; text-transform: uppercase; letter-spacing: 1px; }
    .contact-form { display: flex; flex-direction: column; gap: 14px; }
    .contact-form input, .contact-form textarea { background: var(--bg); border: 1px solid var(--border); color: var(--cream); padding: 14px 16px; font-family: var(--font-body); font-size: .9rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--amber); }
    .contact-form button { padding: 16px; background: var(--red); color: var(--cream); font-family: var(--font-heading); font-weight: 600; font-size: 1rem; letter-spacing: 1px; border: none; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(185,28,28,.25); }

    /* FOOTER */
    .footer { background: var(--bg-darker); border-top: 1px solid var(--border); padding: 48px 0; text-align: center; }
    .footer-brand { font-family: var(--font-heading); font-weight: 700; font-size: 1.2rem; color: var(--cream); margin-bottom: 6px; }
    .footer p { color: var(--cream-muted); font-size: .85rem; }
    .footer-flourish { color: var(--amber); font-size: .75rem; letter-spacing: 4px; margin-bottom: 12px; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(28,25,23,.98); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .about-timeline { padding-left: 32px; }
      .gallery-masonry { grid-template-columns: repeat(2, 1fr); }
      .contact-info-block { flex-direction: column; }
      .menu-item { flex: 0 0 240px; }
    }
    @media (max-width: 480px) {
      .gallery-masonry { grid-template-columns: 1fr; }
      .hero-bottom-bar h1 { font-size: 1.3rem; }
      .menu-item { flex: 0 0 220px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero${heroImage ? '' : ' hero-no-image'}">
    <div class="hero-image-fill">
      ${heroImage ? `<div class="hero-bg" style="background-image:url('${escapeAttr(heroImage)}')"></div>` : ''}
      ${heroImage ? `<div class="hero-overlay"></div>` : ''}
    </div>
    <div class="hero-bottom-bar">
      <div class="hero-bottom-bar-inner">
        <h1>${safeName}</h1>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Reserve a Table</a>` : ''}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <div class="footer-flourish">&laquo; &diams; &raquo;</div>
      <div class="footer-brand">${safeName}</div>
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
